import { useEffect } from 'react';

/**
 * Global scroll management component
 * Provides emergency cleanup for scroll lock issues
 */
const ScrollManager = () => {
    useEffect(() => {
        // Emergency cleanup function
        const emergencyCleanup = () => {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.body.classList.remove('no-scroll');
        };

        // Check if any modals or overlays are actually visible
        const hasActiveOverlays = () => {
            const selectors = [
                '.image-viewer.show',
                '.cart-overlay.visible',
                '.cart-overlay.fade-in',
                '.modal-overlay',
                '.menu-overlay.visible',
                '.generate-variants-modal',
                '.discount-modal',
                '[role="dialog"]',
                '[aria-modal="true"]'
            ];

            return selectors.some(selector => {
                const element = document.querySelector(selector);
                if (!element) return false;

                // Check if element is actually visible
                const styles = window.getComputedStyle(element);
                const isVisible = styles.display !== 'none' &&
                    styles.visibility !== 'hidden' &&
                    styles.opacity !== '0';
                return isVisible;
            });
        };

        // Listen for visibility changes (tab switching, window focus/blur)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                // Small delay to allow components to settle
                setTimeout(() => {
                    if (!hasActiveOverlays()) {
                        emergencyCleanup();
                    }
                }, 100);
            }
        };

        // Listen for page unload
        const handleBeforeUnload = () => {
            emergencyCleanup();
        };

        // Listen for route changes (using popstate as a fallback)
        const handlePopState = () => {
            setTimeout(() => {
                if (!hasActiveOverlays()) {
                    emergencyCleanup();
                }
            }, 100);
        };

        // Periodic check for stuck scroll locks (emergency fallback)
        const periodicCheck = setInterval(() => {
            const bodyHasScrollLock = document.body.classList.contains('no-scroll') ||
                document.body.style.overflow === 'hidden';

            if (bodyHasScrollLock && !hasActiveOverlays()) {
                console.warn('ScrollManager: Detected stuck scroll lock, cleaning up');
                emergencyCleanup();
            }
        }, 5000); // Check every 5 seconds

        // Add event listeners
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('popstate', handlePopState);

        // Initial check
        setTimeout(() => {
            if (!hasActiveOverlays()) {
                emergencyCleanup();
            }
        }, 1000);

        // Cleanup on unmount
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('popstate', handlePopState);
            clearInterval(periodicCheck);
        };
    }, []);

    return null;
};

export default ScrollManager;
